import { useEffect, useState } from "react";
import { Call, Device } from "@twilio/voice-sdk";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openTelephonyCall } from "@/lib/use-incoming-call";

/**
 * GWC Voice — browser softphone engine.
 *
 * Mirrors the CallBus singleton pattern in use-incoming-call.ts: a module-level
 * engine owns the Twilio `Device`, a `useSoftphone()` hook subscribes to it, and
 * a `<SoftphoneHost/>` component (rendered once in the office shell) drives its
 * lifecycle. No React context / provider wrapper needed.
 *
 * The engine stays completely dark until the server hands out a token — the
 * `/api/voice/token` endpoint 503s when the Twilio Voice env isn't configured,
 * so `start()` simply no-ops and the office keeps working exactly as before.
 *
 * Division of labour with the existing screen-pop: the server voice webhook
 * fires the `call:inbound` SSE that pops the Call Panel (with the matched
 * customer). This engine does NOT drive the pop — it only owns the live audio
 * and the answer/decline/hang-up controls, which the Call Panel calls into when
 * a real call is present (`hasCall`).
 */

type Listener = () => void;

class SoftphoneEngine {
    private device: Device | null = null;
    private call: Call | null = null;
    private starting = false;
    ready = false;
    hasCall = false;
    muted = false;
    conferenceMode = false;
    trainingEnabled = false;

    // When the office starts a training call, the next incoming leg (our own
    // softphone, rung by the server) is auto-answered and shown as a training
    // session — so it's one click, not "wait for it to ring, then answer".
    private trainingPending = false;
    private trainingTimer: ReturnType<typeof setTimeout> | null = null;

    private listeners = new Set<Listener>();
    subscribe(fn: Listener): () => void {
        this.listeners.add(fn);
        return () => {
            this.listeners.delete(fn);
        };
    }
    private emit() {
        for (const fn of this.listeners) fn();
    }

    private async fetchToken(): Promise<{ token: string; conferenceMode: boolean; trainingEnabled: boolean } | null> {
        try {
            const res = await api.post<{ token: string; conferenceMode?: boolean; trainingEnabled?: boolean }>(
                "/api/voice/token",
                {},
            );
            if (!res.token) return null;
            return { token: res.token, conferenceMode: !!res.conferenceMode, trainingEnabled: !!res.trainingEnabled };
        } catch {
            // 503 (not configured) or network — stay dark.
            return null;
        }
    }

    async start(): Promise<void> {
        if (this.device || this.starting) return;
        this.starting = true;
        try {
            const fetched = await this.fetchToken();
            if (!fetched || this.device) return;
            this.conferenceMode = fetched.conferenceMode;
            this.trainingEnabled = fetched.trainingEnabled;
            const device = new Device(fetched.token, { closeProtection: true });
            this.device = device;
            device.on("registered", () => {
                this.ready = true;
                this.emit();
            });
            device.on("unregistered", () => {
                this.ready = false;
                this.emit();
            });
            device.on("error", (e) => console.error("[softphone] device error", e));
            device.on("tokenWillExpire", async () => {
                const fresh = await this.fetchToken();
                if (fresh) device.updateToken(fresh.token);
            });
            device.on("incoming", (call: Call) => this.handleIncoming(call));
            await device.register();
        } catch (e) {
            console.error("[softphone] start failed", e);
        } finally {
            this.starting = false;
        }
    }

    stop(): void {
        try {
            this.device?.destroy();
        } catch {
            // ignore teardown errors
        }
        this.device = null;
        this.call = null;
        this.ready = false;
        this.hasCall = false;
        this.clearTrainingPending();
        this.emit();
    }

    private attach(call: Call) {
        this.call = call;
        this.hasCall = true;
        this.muted = false;
        this.emit();
        const clear = () => {
            if (this.call === call) {
                this.call = null;
                this.hasCall = false;
                this.muted = false;
                this.emit();
            }
        };
        call.on("disconnect", clear);
        call.on("cancel", clear);
        call.on("reject", clear);
        call.on("error", clear);
        // Attribute the call to this office user once it connects (inbound
        // answered or outbound placed), keyed on the Twilio CallSid.
        call.on("accept", () => {
            const sid = call.parameters?.CallSid;
            if (sid) void api.post("/api/call-logs/attribute", { callSid: sid }).catch(() => {});
        });
    }

    /**
     * Route an incoming Device leg. A normal inbound call is attached and left
     * for the Call Panel to answer. A training call (one we just asked the server
     * to place at ourselves) is auto-answered and shown as an active training
     * session, so the office doesn't have to catch its own ring.
     */
    private handleIncoming(call: Call): void {
        const training = this.trainingPending;
        this.clearTrainingPending();
        this.attach(call);
        if (!training) return;
        try {
            call.accept();
        } catch {
            // ignore — the panel's Answer still works as a fallback
        }
        openTelephonyCall({
            id: `training-${Date.now()}`,
            fromNumber: "",
            fromName: "Training call — simulated customer",
            callSid: call.parameters?.CallSid,
            receivedAt: Date.now(),
            status: "active",
            origin: "telephony",
            callStartedAt: Date.now(),
            training: true,
        });
    }

    private clearTrainingPending(): void {
        this.trainingPending = false;
        if (this.trainingTimer) {
            clearTimeout(this.trainingTimer);
            this.trainingTimer = null;
        }
    }

    /**
     * Start a training call: ask the server to ring our own softphone with the AI
     * customer on the line, and arm auto-answer for that incoming leg. Returns
     * false if training isn't available or the call couldn't be placed.
     */
    async startTraining(opts?: { scenarioId?: string; brief?: string; opening?: string }): Promise<boolean> {
        if (!this.device || !this.trainingEnabled || this.hasCall) return false;
        this.clearTrainingPending();
        this.trainingPending = true;
        // Safety valve: if the call never arrives, don't auto-answer a later real call.
        this.trainingTimer = setTimeout(() => this.clearTrainingPending(), 25_000);
        try {
            await api.post("/api/voice/training/start", opts ?? {});
            return true;
        } catch {
            this.clearTrainingPending();
            return false;
        }
    }

    /** Answer the ringing inbound call. */
    answer(): void {
        this.call?.accept();
    }
    /** Decline a ringing call (send to voicemail). */
    reject(): void {
        const call = this.call;
        if (!call) return;
        try {
            call.reject();
        } catch {
            call.disconnect();
        }
    }
    /** Hang up the connected call. */
    hangup(): void {
        this.call?.disconnect();
    }
    /** Toggle (or set) mute on our own mic. The caller can't hear us; we still hear them. */
    toggleMute(next?: boolean): void {
        const call = this.call;
        if (!call) return;
        const want = next ?? !this.muted;
        call.mute(want);
        this.muted = call.isMuted();
        this.emit();
    }
    /** Send DTMF touch-tones (e.g. to navigate an automated phone menu). */
    sendDigits(digits: string): void {
        this.call?.sendDigits(digits);
    }
    /** Place an outbound call from the office softphone. */
    async dial(phoneNumber: string): Promise<void> {
        if (!this.device) return;
        const call = await this.device.connect({ params: { PhoneNumber: phoneNumber } });
        this.attach(call);
    }
}

const engine = new SoftphoneEngine();

/**
 * Drives the softphone Device lifecycle. Render once in the office shell; it
 * renders nothing. Only office users start a device; techs never do.
 */
export function SoftphoneHost() {
    const { isOffice } = useAuth();
    useEffect(() => {
        if (!isOffice) return;
        void engine.start();
        return () => engine.stop();
    }, [isOffice]);
    return null;
}

export interface SoftphoneControls {
    /** Device registered and able to send/receive calls. */
    ready: boolean;
    /** A live Twilio call is present (ringing or connected). */
    hasCall: boolean;
    /** Our mic is currently muted. */
    muted: boolean;
    /** Server is in conference mode — hold + warm hand-off are available. */
    conferenceMode: boolean;
    /** Training Simulator is configured — the office can start a practice call. */
    trainingEnabled: boolean;
    answer: () => void;
    reject: () => void;
    hangup: () => void;
    toggleMute: (next?: boolean) => void;
    sendDigits: (digits: string) => void;
    dial: (phoneNumber: string) => Promise<void>;
    /** Place a training call to our own softphone and auto-answer it. */
    startTraining: (opts?: { scenarioId?: string; brief?: string; opening?: string }) => Promise<boolean>;
}

/** Subscribe to the softphone engine and get its controls. */
export function useSoftphone(): SoftphoneControls {
    const [, force] = useState(0);
    useEffect(() => engine.subscribe(() => force((n) => n + 1)), []);
    return {
        ready: engine.ready,
        hasCall: engine.hasCall,
        muted: engine.muted,
        conferenceMode: engine.conferenceMode,
        trainingEnabled: engine.trainingEnabled,
        answer: () => engine.answer(),
        reject: () => engine.reject(),
        hangup: () => engine.hangup(),
        toggleMute: (next?: boolean) => engine.toggleMute(next),
        sendDigits: (digits: string) => engine.sendDigits(digits),
        dial: (n: string) => engine.dial(n),
        startTraining: (opts?: { scenarioId?: string; brief?: string; opening?: string }) => engine.startTraining(opts),
    };
}
